import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Upload,
  FileText,
  CheckCircle,
  Clock,
  TrendingUp,
  ShoppingCart,
  Receipt,
  ChevronRight,
  File,
  Image,
  FileType,
  DollarSign,
  Building,
  Calendar,
  Shield,
  Users,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [stats, setStats] = useState({
    pending: 0,
    verified: 0,
    total: 0,
    totalAmount: 0,
    purchaseInvoices: 0,
    salesInvoices: 0,
  });
  const [invoiceType, setInvoiceType] = useState("purchase");

  React.useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/invoices`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const invoices = response.data;
      setRecentInvoices(invoices.slice(0, 5));

      const pending = invoices.filter((inv) => inv.status === "pending").length;
      const verified = invoices.filter(
        (inv) => inv.status === "verified",
      ).length;
      const total = invoices.length;
      const totalAmount = invoices.reduce(
        (sum, inv) => sum + (inv.extracted_data?.total_amount || 0),
        0,
      );
      const purchaseInvoices = invoices.filter(
        (inv) => inv.invoice_type === "purchase",
      ).length;
      const salesInvoices = invoices.filter(
        (inv) => inv.invoice_type === "sales",
      ).length;

      setStats({
        pending,
        verified,
        total,
        totalAmount,
        purchaseInvoices,
        salesInvoices,
      });
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    }
  };

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (acceptedFiles.length === 0) return;

      setUploading(true);
      setUploadProgress(0);

      try {
        const formData = new FormData();

        if (acceptedFiles.length === 1) {
          // Single file upload
          formData.append("file", acceptedFiles[0]);
          const token = localStorage.getItem("token");
          const response = await axios.post(
            `${API}/invoices/upload?invoice_type=${invoiceType}`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "multipart/form-data",
              },
            },
          );
          toast.success("Invoice uploaded and processed successfully!");
          navigate(`/verify/${response.data.id}`);
        } else {
          // Batch upload
          acceptedFiles.forEach((file) => {
            formData.append("files", file);
          });

          const token = localStorage.getItem("token");
          const response = await axios.post(
            `${API}/invoices/batch-upload?invoice_type=${invoiceType}`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "multipart/form-data",
              },
              onUploadProgress: (progressEvent) => {
                const percentCompleted = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total,
                );
                setUploadProgress(percentCompleted);
              },
            },
          );

          // Show detailed results
          const { successful, total_files, skipped, skipped_invoices, failed, errors } = response.data;
          
          if (successful > 0) {
            toast.success(
              `${successful}/${total_files} ${invoiceType} invoices uploaded successfully!`
            );
          }

          // Show warning for skipped invoices (wrong type)
          if (skipped > 0 && skipped_invoices && skipped_invoices.length > 0) {
            const skippedDetails = skipped_invoices
              .map(item => `• ${item.filename} (${item.invoice_no}): Wrong invoice type`)
              .join('\n');
            toast.warning(
              `⚠️ ${skipped} invoice(s) skipped - not ${invoiceType} invoices\n\n${skippedDetails}`,
              { duration: 8000 }
            );
          }

          // Show errors for failed uploads
          if (failed > 0 && errors && errors.length > 0) {
            const errorDetails = errors
              .map(item => `• ${item.filename}: ${item.error}`)
              .join('\n');
            toast.error(
              `❌ ${failed} invoice(s) failed\n\n${errorDetails}`,
              { duration: 8000 }
            );
          }

          loadDashboardData();
          navigate("/invoices");
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || "Upload failed");
      } finally {
        setUploading(false);
        setUploadProgress(0);
      }
    },
    [navigate, invoiceType],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png"],
      "application/pdf": [".pdf"],
    },
    maxFiles: 20,
    disabled: uploading,
  });

  const getStatusColor = (status) => {
    switch (status) {
      case "verified":
        return "bg-gradient-to-r from-green-500/10 to-green-500/5 border-l-2 border-green-500 text-green-700";
      case "pending":
        return "bg-gradient-to-r from-orange-500/10 to-orange-500/5 border-l-2 border-orange-500 text-orange-700";
      case "exported":
        return "bg-gradient-to-r from-primary-500/10 to-primary-500/5 border-l-2 border-primary-500 text-primary-700";
      default:
        return "bg-gradient-to-r from-gray-100/50 to-gray-50/50 border-l-2 border-gray-300 text-gray-700";
    }
  };

  const getTypeIcon = (type) => {
    return type === "sales" ? (
      <div className="p-1.5 rounded-md bg-gradient-to-br from-green-100 to-green-50">
        <Receipt size={14} className="text-green-600" />
      </div>
    ) : (
      <div className="p-1.5 rounded-md bg-gradient-to-br from-red-100 to-red-50">
        <ShoppingCart size={14} className="text-red-600" />
      </div>
    );
  };

  const formatCurrency = (amount) => {
    return `₹${amount.toLocaleString("en-IN", {
      maximumFractionDigits: 0,
    })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
    });
  };

  return (
    <Layout>
      <div className="space-y-4" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-primary-600 to-orange-600 bg-clip-text text-transparent">
              Dashboard Overview
            </h1>
            <p className="text-gray-500 text-xs mt-0.5">
              Upload, extract, and verify invoice data with AI-powered
              automation
            </p>
          </div>
          <Button
            onClick={() => navigate("/reports")}
            data-testid="view-reports-btn"
            className="bg-gradient-to-r from-primary-500 to-orange-500 hover:from-primary-600 hover:to-orange-600 text-white text-xs sm:text-sm px-3 py-1.5 h-auto"
          >
            <TrendingUp size={12} className="mr-1.5" />
            View Reports
          </Button>
        </div>

        {/* Stats Cards - Solid Gradient Design */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-2">
          {/* Total Invoices */}
          <Card className="border-0 bg-gradient-to-br from-primary-400 to-primary-500 text-white group hover:from-primary-500 hover:to-primary-600 transition-all duration-300">
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium">Total</span>
                <div className="p-1.5 rounded-md bg-white/20 backdrop-blur-sm">
                  <FileText size={12} />
                </div>
              </div>
              <div className="text-lg font-bold">{stats.total}</div>
              <p className="text-[10px] text-white/90 mt-0.5">Invoices</p>
            </CardContent>
          </Card>

          {/* Purchase */}
          <Card className="border-0 bg-gradient-to-br from-red-400 to-red-500 text-white group hover:from-red-500 hover:to-red-600 transition-all duration-300">
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium">Purchase</span>
                <div className="p-1.5 rounded-md bg-white/20 backdrop-blur-sm">
                  <ShoppingCart size={12} />
                </div>
              </div>
              <div className="text-lg font-bold">{stats.purchaseInvoices}</div>
              <p className="text-[10px] text-white/90 mt-0.5">Invoices</p>
            </CardContent>
          </Card>

          {/* Sales */}
          <Card className="border-0 bg-gradient-to-br from-green-400 to-green-500 text-white group hover:from-green-500 hover:to-green-600 transition-all duration-300">
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium">Sales</span>
                <div className="p-1.5 rounded-md bg-white/20 backdrop-blur-sm">
                  <Receipt size={12} />
                </div>
              </div>
              <div className="text-lg font-bold">{stats.salesInvoices}</div>
              <p className="text-[10px] text-white/90 mt-0.5">Invoices</p>
            </CardContent>
          </Card>

          {/* Pending */}
          <Card className="border-0 bg-gradient-to-br from-orange-400 to-orange-500 text-white group hover:from-orange-500 hover:to-orange-600 transition-all duration-300">
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium">Pending</span>
                <div className="p-1.5 rounded-md bg-white/20 backdrop-blur-sm">
                  <Clock size={12} />
                </div>
              </div>
              <div className="text-lg font-bold">{stats.pending}</div>
              <p className="text-[10px] text-white/90 mt-0.5">To Verify</p>
            </CardContent>
          </Card>

          {/* Verified */}
          <Card className="border-0 bg-gradient-to-br from-emerald-400 to-emerald-500 text-white group hover:from-emerald-500 hover:to-emerald-600 transition-all duration-300">
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium">Verified</span>
                <div className="p-1.5 rounded-md bg-white/20 backdrop-blur-sm">
                  <CheckCircle size={12} />
                </div>
              </div>
              <div className="text-lg font-bold">{stats.verified}</div>
              <p className="text-[10px] text-white/90 mt-0.5">Processed</p>
            </CardContent>
          </Card>

          {/* Total Value */}
          <Card className="border-0 bg-gradient-to-br from-purple-400 to-purple-500 text-white group hover:from-purple-500 hover:to-purple-600 transition-all duration-300">
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium">Total Value</span>
                <div className="p-1.5 rounded-md bg-white/20 backdrop-blur-sm">
                  <DollarSign size={12} />
                </div>
              </div>
              <div className="text-lg font-bold">
                {formatCurrency(stats.totalAmount)}
              </div>
              <p className="text-[10px] text-white/90 mt-0.5">Cumulative</p>
            </CardContent>
          </Card>
        </div>

        {/* Upload Section - Enhanced Design */}
        <div className={`grid gap-4 ${JSON.parse(localStorage.getItem("user") || "{}").account_type !== "organization" ? "lg:grid-cols-3" : "lg:grid-cols-1"}`}>
          {JSON.parse(localStorage.getItem("user") || "{}").account_type !== "organization" && (
          <Card className="lg:col-span-2 border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
            <CardHeader className="pb-3 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <CardTitle className="text-sm font-bold text-gray-800 flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-gradient-to-br from-primary-400 to-primary-500">
                      <Upload size={14} className="text-white" />
                    </div>
                    Upload Invoices
                  </CardTitle>
                  <CardDescription className="text-xs text-gray-500">
                    Single or batch upload • Max 20 files • PDF, JPG, PNG
                  </CardDescription>
                </div>
                <div className="flex gap-1">
                  <Badge variant="outline" className="text-xs bg-white">
                    <File size={10} className="mr-1" />
                    PDF
                  </Badge>
                  <Badge variant="outline" className="text-xs bg-white">
                    <Image size={10} className="mr-1" />
                    Images
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-4">
                {/* Invoice Type Selector */}
                <div className="flex gap-1">
                  <button
                    onClick={() => setInvoiceType("purchase")}
                    data-testid="purchase-tab"
                    className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg border text-xs font-medium transition-all duration-200 ${
                      invoiceType === "purchase"
                        ? "bg-gradient-to-r from-primary-500 to-orange-500 text-white border-transparent shadow-sm"
                        : "bg-white border-gray-200 text-gray-600 hover:border-primary-300 hover:text-primary-600"
                    }`}
                  >
                    <ShoppingCart size={12} />
                    Purchase
                  </button>
                  <button
                    onClick={() => setInvoiceType("sales")}
                    data-testid="sales-tab"
                    className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg border text-xs font-medium transition-all duration-200 ${
                      invoiceType === "sales"
                        ? "bg-gradient-to-r from-green-500 to-emerald-500 text-white border-transparent shadow-sm"
                        : "bg-white border-gray-200 text-gray-600 hover:border-green-300 hover:text-green-600"
                    }`}
                  >
                    <Receipt size={12} />
                    Sales
                  </button>
                </div>

                {/* Upload Dropzone */}
                <div
                  {...getRootProps()}
                  data-testid="invoice-upload-dropzone"
                  className={`
            relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all duration-300
            ${
              isDragActive
                ? "border-primary-500 bg-gradient-to-br from-primary-50 to-primary-100/50"
                : invoiceType === "purchase"
                  ? "border-primary-300 hover:border-primary-500 bg-gradient-to-br from-primary-50/30 to-primary-100/20"
                  : "border-green-300 hover:border-green-500 bg-gradient-to-br from-green-50/30 to-green-100/20"
            }
            ${uploading ? "opacity-60 cursor-not-allowed" : "hover:shadow-sm"}
          `}
                >
                  <input {...getInputProps()} />

                  {/* Decorative background pattern */}
                  <div className="absolute inset-0 overflow-hidden rounded-xl opacity-5">
                    <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-gradient-to-br from-primary-400 to-orange-400"></div>
                    <div className="absolute -bottom-6 -left-6 w-24 h-24 rounded-full bg-gradient-to-br from-primary-400 to-orange-400"></div>
                  </div>

                  {uploading ? (
                    <div className="space-y-3 relative z-10">
                      <div className="flex justify-center">
                        <div className="relative">
                          <div className="p-3 rounded-full bg-gradient-to-br from-primary-400/20 to-orange-400/20 animate-pulse">
                            <Upload className="text-primary-500" size={22} />
                          </div>
                          <div className="absolute inset-0 rounded-full border-2 border-primary-500/30 border-t-transparent animate-spin"></div>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-700 mb-1">
                          Processing invoices...
                        </p>
                        {uploadProgress > 0 && (
                          <div className="space-y-1">
                            <div className="flex justify-between text-xs text-gray-500">
                              <span>Uploading</span>
                              <span>{uploadProgress}%</span>
                            </div>
                            <Progress
                              value={uploadProgress}
                              className="h-1.5 bg-gray-200"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ) : isDragActive ? (
                    <div className="space-y-3 relative z-10">
                      <div className="flex justify-center">
                        <div className="p-3 rounded-full bg-gradient-to-br from-primary-400/20 to-orange-400/20">
                          <Upload
                            className="text-primary-500 animate-bounce"
                            size={22}
                          />
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-primary-600">
                          Drop to upload
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Release files to start processing
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4 relative z-10">
                      <div className="flex justify-center">
                        <div className="p-3 rounded-full bg-gradient-to-br from-primary-50 to-orange-50">
                          <Upload className="text-primary-500" size={22} />
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-800">
                          Drag & drop files here
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          or{" "}
                          <span className="font-semibold text-primary-600 underline">
                            click to browse
                          </span>
                        </p>
                      </div>
                      <div className="pt-2">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-gray-200">
                          <div className="flex items-center gap-1 text-xs text-gray-600">
                            <File size={10} />
                            <span>Max 20 files</span>
                          </div>
                          <div className="w-1 h-1 rounded-full bg-gray-300"></div>
                          <div className="flex items-center gap-1 text-xs text-gray-600">
                            <Image size={10} />
                            <span>10MB each</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          )}

          {/* Quick Actions - Enhanced */}
          <Card className={`border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm ${JSON.parse(localStorage.getItem("user") || "{}").account_type !== "organization" ? "" : "lg:col-span-1"}`}>
            <CardHeader className="pb-3 border-b border-gray-100">
              <CardTitle className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-gradient-to-br from-primary-400 to-primary-500">
                  <TrendingUp size={14} className="text-white" />
                </div>
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-2">
              {[
                ...(JSON.parse(localStorage.getItem("user") || "{}").account_type !== "organization" ? [{
                  icon: FileText,
                  label: "Manual Entry",
                  path: "/manual-entry",
                  color: "from-blue-500 to-cyan-500",
                }] : []),
                {
                  icon: Building,
                  label: "Bank Recon",
                  path: "/bank-reconciliation",
                  color: "from-purple-500 to-pink-500",
                },
                {
                  icon: Receipt,
                  label: "All Invoices",
                  path: "/invoices",
                  color: "from-green-500 to-emerald-500",
                },
                ...((JSON.parse(localStorage.getItem("user") || "{}").account_type === "client" ||
                  JSON.parse(localStorage.getItem("user") || "{}").account_type === "team_member")
                  ? [{
                      icon: Users,
                      label: "Customer Master",
                      path: "/customer-master",
                      color: "from-cyan-500 to-blue-500",
                    }]
                  : []),
                {
                  icon: TrendingUp,
                  label: "GST Reports",
                  path: "/reports",
                  color: "from-orange-500 to-amber-500",
                },
                ...(JSON.parse(localStorage.getItem("user") || "{}").account_type === "organization" ? [{
                  icon: Users,
                  label: "My Clients",
                  path: "/team-members",
                  color: "from-indigo-500 to-purple-500",
                }] : []),
              ].map((action) => (
                <button
                  key={action.path}
                  onClick={() => navigate(action.path)}
                  className="group w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-white hover:border-transparent hover:shadow-sm transition-all duration-200"
                >
                  <div
                    className={`p-2 rounded-md bg-gradient-to-br ${action.color}`}
                  >
                    <action.icon size={12} className="text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-xs font-medium text-gray-800 group-hover:text-gray-900">
                      {action.label}
                    </p>
                    <p className="text-[10px] text-gray-500">
                      Click to navigate
                    </p>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <div className="w-5 h-5 rounded-full bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-gray-400"></div>
                    </div>
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Recent Invoices - Enhanced */}
        <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
          <CardHeader className="pb-3 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <CardTitle className="text-sm font-bold text-gray-800 flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-gradient-to-br from-primary-400 to-primary-500">
                    <FileText size={14} className="text-white" />
                  </div>
                  Recent Invoices
                </CardTitle>
                <CardDescription className="text-xs text-gray-500">
                  Latest processed invoices requiring attention
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate("/invoices")}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                >
                  View all →
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            {recentInvoices.length === 0 ? (
              <div className="text-center py-6">
                <div className="mx-auto w-12 h-12 rounded-full bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center mb-3">
                  <FileText size={18} className="text-gray-400" />
                </div>
                <p className="text-sm font-medium text-gray-600 mb-1">
                  No invoices yet
                </p>
                <p className="text-xs text-gray-500">
                  Upload your first invoice to get started
                </p>
                <Button
                  onClick={() => navigate("/")}
                  className="mt-4 h-7 text-xs bg-gradient-to-r from-primary-500 to-orange-500 hover:from-primary-600 hover:to-orange-600 text-white"
                >
                  <Upload size={10} className="mr-1.5" />
                  Upload Invoice
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {recentInvoices.map((invoice) => {
                  const invoiceDate = invoice.extracted_data?.invoice_date
                    ? formatDate(invoice.extracted_data.invoice_date)
                    : "N/A";
                  const amount = formatCurrency(
                    invoice.extracted_data?.total_amount || 0,
                  );

                  return (
                    <div
                      key={invoice.id}
                      data-testid={`recent-invoice-${invoice.id}`}
                      className="group relative overflow-hidden rounded-xl bg-white border border-gray-200 hover:border-primary-300 hover:shadow-sm transition-all duration-300 cursor-pointer"
                      onClick={() => navigate(`/verify/${invoice.id}`)}
                    >
                      {/* Hover effect background */}
                      <div className="absolute inset-0 bg-gradient-to-r from-primary-50/0 to-orange-50/0 group-hover:from-primary-50/30 group-hover:to-orange-50/30 transition-all duration-300"></div>

                      <div className="relative p-3">
                        <div className="flex items-start justify-between gap-3">
                          {/* Left side - Details */}
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <div className="shrink-0">
                              {invoice.invoice_type === "sales" ? (
                                <div className="p-2 rounded-lg bg-gradient-to-br from-green-400/10 to-emerald-400/10 border border-green-200">
                                  <Receipt
                                    size={14}
                                    className="text-green-600"
                                  />
                                </div>
                              ) : (
                                <div className="p-2 rounded-lg bg-gradient-to-br from-red-400/10 to-orange-400/10 border border-red-200">
                                  <ShoppingCart
                                    size={14}
                                    className="text-red-600"
                                  />
                                </div>
                              )}
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="text-xs font-semibold text-gray-800 truncate">
                                  {invoice.extracted_data?.supplier_name ||
                                    invoice.extracted_data?.buyer_name ||
                                    "Unknown Party"}
                                </h4>
                                <div
                                  className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${getStatusColor(invoice.status)}`}
                                >
                                  {invoice.status.charAt(0).toUpperCase() +
                                    invoice.status.slice(1)}
                                </div>
                              </div>

                              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] text-gray-500">
                                <div className="flex items-center gap-1">
                                  <div className="w-1.5 h-1.5 rounded-full bg-gray-300"></div>
                                  <span>
                                    #
                                    {invoice.extracted_data?.invoice_no ||
                                      "N/A"}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Calendar size={8} />
                                  <span>{invoiceDate}</span>
                                </div>
                                {invoice.extracted_data?.supplier_gst_no && (
                                  <div className="flex items-center gap-1">
                                    <Shield size={8} />
                                    <span className="font-mono">
                                      {invoice.extracted_data.supplier_gst_no}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Right side - Amount & Action */}
                          <div className="shrink-0 flex flex-col items-end gap-1">
                            <div className="text-right">
                              <div className="text-sm font-bold bg-gradient-to-r from-gray-800 to-gray-900 bg-clip-text text-transparent">
                                {amount}
                              </div>
                              <div className="text-[10px] text-gray-500 mt-0.5">
                                Total amount
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="text-[10px] text-gray-400 group-hover:text-primary-500">
                                View details
                              </div>
                              <ChevronRight
                                size={10}
                                className="text-gray-400 group-hover:text-primary-500 group-hover:translate-x-0.5 transition-all duration-200"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Confidence indicator (if available) */}
                        {invoice.confidence_scores && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <div className="flex items-center justify-between">
                              <div className="text-[10px] text-gray-500">
                                Processing confidence
                              </div>
                              <div className="flex items-center gap-1.5">
                                <div className="w-16 h-1 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-gradient-to-r from-green-400 to-green-500"
                                    style={{
                                      width: `${
                                        (Object.values(
                                          invoice.confidence_scores,
                                        ).reduce((a, b) => a + b, 0) /
                                          8) *
                                        100
                                      }%`,
                                    }}
                                  ></div>
                                </div>
                                <span className="text-[10px] font-medium text-gray-700">
                                  {Math.round(
                                    (Object.values(
                                      invoice.confidence_scores,
                                    ).reduce((a, b) => a + b, 0) /
                                      8) *
                                      100,
                                  )}
                                  %
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Quick Stats Bar */}
                {recentInvoices.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                          <span className="text-gray-600">
                            Verified:{" "}
                            <span className="font-semibold">
                              {
                                recentInvoices.filter(
                                  (inv) => inv.status === "verified",
                                ).length
                              }
                            </span>
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                          <span className="text-gray-600">
                            Pending:{" "}
                            <span className="font-semibold">
                              {
                                recentInvoices.filter(
                                  (inv) => inv.status === "pending",
                                ).length
                              }
                            </span>
                          </span>
                        </div>
                      </div>
                      <Button
                        onClick={() => navigate("/invoices")}
                        variant="outline"
                        data-testid="view-all-invoices-btn"
                        className="h-7 text-xs border-gray-300 hover:border-primary-400 hover:bg-primary-50"
                      >
                        <FileText size={10} className="mr-1.5" />
                        Manage All Invoices
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
